import type { TensorflowModel } from '@/ml/tflite';

import { TirvuFace, type DetectedFace, type Tile } from '@/ml/tirvu-face';

// Classificação de expressão facial — modelo FER+ (ferplus_model_pd_best.tflite,
// repo vicksam/fer-model), no MESMO padrão de FaceRecognition/FaceAntiSpoof.
//   Entrada : 48x48x1 (grayscale), pixel/255 → [0,1]
//   Saída   : 8 floats (probabilidades); argmax = classe
//   Ordem   : 0 neutral, 1 happiness, 2 surprise, 3 sadness, 4 anger, 5 disgust, 6 fear, 7 contempt
const MODEL_INPUT_SIZE = 48;
const NUM_CLASSES = 8;

// Índice da classe FER+ → valor do campo `expressao`. O domínio não tem "desprezo",
// então contempt cai em "padrão".
const EXPRESSAO_POR_CLASSE = [
  'padrão', // 0 neutral
  'feliz', // 1 happiness
  'surpreso', // 2 surprise
  'triste', // 3 sadness
  'nervoso', // 4 anger
  'com nojo', // 5 disgust
  'com medo', // 6 fear
  'padrão', // 7 contempt (sem rótulo próprio)
];

/**
 * Spec do tile deste modelo. O orquestrador pede junto com os demais, numa passada só.
 *
 * O grayscale agora sai do NATIVO, com a mesma fórmula de luma Rec.601 que este
 * arquivo usava (0.299R + 0.587G + 0.114B) — a distribuição que o FER+ vê não muda.
 * O que muda é o recorte: passou a ser QUADRADO em vez da caixa retangular esticada
 * para 48x48. É mais fiel ao FER2013, que é treinado em recortes quadrados.
 */
export const SPEC_EXPRESSAO = {
  size: MODEL_INPUT_SIZE,
  mode: 'box' as const,
  // 10% de folga — o FER é treinado em rostos bem enquadrados. Mesmo valor de antes.
  padding: 0.1,
  grayscale: true,
};

let modelPromise: Promise<TensorflowModel | null> | null = null;

const loadModel = (): Promise<TensorflowModel | null> => {
  if (!modelPromise) {
    modelPromise = (async () => {
      try {
        const { loadTensorflowModel } = await import('@/ml/tflite');
        return await loadTensorflowModel("/models/emotion.tflite" /* WEB: modelo servido pelo PWA */);
      } catch {
        return null;
      }
    })();
  }
  return modelPromise;
};

/** Grayscale [0,255] → [0,1], 1 canal — formato esperado pelo FER+. */
const buildModelInput = (gray: Uint8Array): Float32Array => {
  const total = MODEL_INPUT_SIZE * MODEL_INPUT_SIZE;
  if (gray.length !== total) {
    throw new Error(`Tile de expressão com ${gray.length} bytes, esperado ${total}`);
  }
  const input = new Float32Array(total);
  for (let i = 0; i < total; i++) input[i] = gray[i] / 255;
  return input;
};

export const FaceExpression = {
  /** Pré-carrega o modelo (chamar no warmup junto com FaceRecognition/AntiSpoof). */
  warmup: async (): Promise<void> => {
    await loadModel();
  },

  /**
   * Classifica a expressão a partir de um tile 48x48 grayscale já recortado.
   * Retorna `null` se o modelo não estiver disponível — o caller decide o default.
   */
  classifyFromTile: async (tile: Tile): Promise<string | null> => {
    const model = await loadModel();
    if (!model) return null;

    const input = buildModelInput(tile.data);

    // Defesa: o fast-tflite só valida shape/dtype em build DEBUG; em release, um
    // model.run com buffer de tamanho/tipo errado pode estourar nativamente (não
    // capturável por try/catch). Validamos no JS p/ degradar (vira null no caller)
    // em vez de fechar o app caso o emotion.tflite seja trocado por outro shape.
    const inT: any = (model as any).inputs?.[0];
    if (inT?.dataType && inT.dataType !== 'float32') {
      throw new Error(`emotion.tflite dtype ${inT.dataType} (esperado float32)`);
    }
    const inElems = Array.isArray(inT?.shape)
      ? inT.shape.reduce((a: number, n: number) => a * (n || 1), 1)
      : 0;
    if (inElems && inElems !== input.length) {
      throw new Error(`emotion.tflite espera ${inElems} elems, recebeu ${input.length}`);
    }

    const outputs = await model.run([input]);
    const probs = outputs[0] as Float32Array;
    if (!probs || probs.length < NUM_CLASSES) return null;

    let best = 0;
    for (let i = 1; i < NUM_CLASSES; i++) {
      if (probs[i] > probs[best]) best = i;
    }
    return EXPRESSAO_POR_CLASSE[best] ?? 'padrão';
  },

  /** Atalho quando não há tile pronto: detecta, recorta e classifica numa chamada. */
  classify: async (imageUri: string, face?: DetectedFace): Promise<string | null> => {
    try {
      let alvo = face;
      if (!alvo) {
        const res = await TirvuFace.detect(imageUri);
        alvo = res.faces[0];
        if (!alvo?.landmarks) return null;
      }
      const [tile] = await TirvuFace.extractTiles(imageUri, alvo, [SPEC_EXPRESSAO]);
      return FaceExpression.classifyFromTile(tile);
    } catch {
      return null;
    }
  },

  LABELS: EXPRESSAO_POR_CLASSE,
};
